import React, { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { Bell, BellOff, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import {
  initializePushNotifications,
  getCurrentSubscription,
  unsubscribeLocal,
  requestNotificationPermission,
} from '../utils/pushNotifications';
import * as api from '../utils/api';

export default function PushNotificationSetup() {
  const { user } = useUser();
  const [status, setStatus] = useState('checking'); // checking, enabled, disabled, error, not-supported
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    checkNotificationStatus();
  }, []);

  const checkNotificationStatus = async () => {
    // Controlla supporto browser
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      setStatus('not-supported');
      return;
    }

    // Controlla permesso
    const currentPermission = Notification.permission;
    setPermission(currentPermission);

    if (currentPermission === 'granted') {
      // Verifica se c'è già una subscription attiva
      try {
        const subscription = await getCurrentSubscription();
        if (subscription) {
          // Verifica anche sul backend
          try {
            const response = await api.checkPushSubscription(user?.id);
            if (response.data?.subscribed) {
              setStatus('enabled');
            } else {
              setStatus('disabled');
            }
          } catch (error) {
            // Se l'endpoint non esiste ancora, considera abilitato se c'è subscription locale
            setStatus('enabled');
          }
        } else {
          setStatus('disabled');
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
        setStatus('disabled');
      }
    } else if (currentPermission === 'denied') {
      setStatus('denied');
    } else {
      setStatus('disabled');
    }
  };

  const enableNotifications = async () => {
    if (!user?.id) {
      setErrorMessage('Devi essere loggato per abilitare le notifiche');
      setStatus('error');
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    setStatus('checking');

    try {
      // Inizializza push notifications
      const result = await initializePushNotifications(user.id);

      if (result.success) {
        // Invia subscription al backend
        try {
          await api.subscribePushNotifications(user.id, result.subscription);
          setStatus('enabled');
          setPermission('granted');
          console.log('✅ Push notifications enabled successfully');
        } catch (error) {
          console.error('Error sending subscription to backend:', error);
          setErrorMessage('Errore nel salvataggio sul server. Riprova più tardi.');
          setStatus('error');
        }
      } else {
        setErrorMessage(result.error || 'Errore nell\'abilitazione delle notifiche');
        setStatus('error');
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      setErrorMessage('Errore nell\'abilitazione delle notifiche');
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const disableNotifications = async () => {
    if (!user?.id) return;

    setLoading(true);
    setStatus('checking');

    try {
      // Rimuovi subscription locale
      await unsubscribeLocal();

      // Rimuovi dal backend
      try {
        await api.unsubscribePushNotifications(user.id);
      } catch (error) {
        console.error('Error unsubscribing from backend:', error);
        // Continua comunque anche se il backend fallisce
      }

      setStatus('disabled');
      console.log('✅ Push notifications disabled');
    } catch (error) {
      console.error('Error disabling notifications:', error);
      setStatus('error');
      setErrorMessage('Errore nella disabilitazione');
    } finally {
      setLoading(false);
    }
  };

  // Not supported
  if (status === 'not-supported') {
    return (
      <div className="flex items-center gap-3 p-4 bg-slate-800/50 border border-slate-700/50 rounded-lg">
        <AlertCircle className="w-5 h-5 text-slate-500" />
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-400">
            Notifiche Push Non Supportate
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            Il tuo browser non supporta le notifiche push
          </p>
        </div>
      </div>
    );
  }

  // Checking
  if (status === 'checking' && loading) {
    return (
      <div className="flex items-center gap-3 p-4 bg-slate-800/50 border border-slate-700/50 rounded-lg">
        <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-300">
            Verifica in corso...
          </p>
        </div>
      </div>
    );
  }

  // Enabled
  if (status === 'enabled') {
    return (
      <div className="flex items-center gap-3 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-emerald-300">
            Notifiche Push Abilitate
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            Riceverai notifiche anche quando il browser è chiuso
          </p>
        </div>
        <button
          onClick={disableNotifications}
          disabled={loading}
          className="px-3 py-1.5 text-sm bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors flex-shrink-0"
        >
          {loading ? '...' : 'Disabilita'}
        </button>
      </div>
    );
  }

  // Denied
  if (permission === 'denied' || status === 'denied') {
    return (
      <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
        <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-red-300">
            Notifiche Bloccate
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            Abilita le notifiche nelle impostazioni del browser per ricevere alert in tempo reale
          </p>
        </div>
      </div>
    );
  }

  // Error
  if (status === 'error') {
    return (
      <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-red-300">
            Errore
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            {errorMessage || 'Si è verificato un errore. Riprova più tardi.'}
          </p>
        </div>
        <button
          onClick={enableNotifications}
          disabled={loading}
          className="px-3 py-1.5 text-sm bg-violet-500 hover:bg-violet-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex-shrink-0"
        >
          {loading ? '...' : 'Riprova'}
        </button>
      </div>
    );
  }

  // Disabled - Show enable button
  return (
    <div className="flex items-center gap-3 p-4 bg-violet-500/10 border border-violet-500/30 rounded-lg">
      <Bell className="w-5 h-5 text-violet-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-violet-300">
          Abilita Notifiche Push
        </p>
        <p className="text-xs text-slate-400 mt-0.5">
          Ricevi notifiche anche quando il browser è chiuso
        </p>
      </div>
      <button
        onClick={enableNotifications}
        disabled={loading}
        className="px-4 py-2 bg-violet-500 hover:bg-violet-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium flex-shrink-0"
      >
        {loading ? '...' : 'Abilita'}
      </button>
    </div>
  );
}




